import { useCallback, useState } from "react";
import { useToast } from "@src/lib/hooks/useToast";
import { gql } from "@apollo/client";
import {
  PresignedS3UploadDataFragment,
  useGeneratePresignedS3PostDataMutation,
} from "./useS3Upload.generated";
import { toKebabCase } from "@src/lib/utils/strings";
import { MAX_FILE_SIZE_BYTES } from "@src/lib/utils/files";
import { ToastErrorInfo } from "@src/lib/contexts/ToastContext";
import { getMediaUrlFromKey } from "@src/features/contentDetail/utils/media";
import { PresignedS3PostDataBucket } from "@src/types.generated";

export type S3Upload = {
  fileId: string;
  url: string;
  name: string;
  key: string;
};

export type S3UploadError = Error & {
  fileId: string;
};

const convertFieldsToKebabCase = (
  fields: Record<string, string>,
): Record<string, string> => {
  const convertedFields: Record<string, string> = {};
  Object.entries(fields)
    .filter(([key]) => key !== "__typename")
    .forEach(([key, value]) => {
      convertedFields[toKebabCase(key)] = value;
    });
  return convertedFields;
};

const normalizeContentType = (file: File): string => {
  let contentType = file.type;
  if (!contentType || contentType === "application/octet-stream") {
    const extension = file.name.toLowerCase().split(".").pop();
    const mimeTypes: Record<string, string> = {
      mov: "video/quicktime",
      mp4: "video/mp4",
      avi: "video/x-msvideo",
      wmv: "video/x-ms-wmv",
      flv: "video/x-flv",
      webm: "video/webm",
    };
    contentType = mimeTypes[extension || ""] || "application/octet-stream";
  }
  return contentType;
};

const validateImage = (file: File): Promise<void> => {
  return new Promise<void>(
    (resolve, reject: (error: ToastErrorInfo) => void) => {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        reject({
          message:
            "Image file size too big. Must be less than 50MB and under 10,000 pixels in both width and height.",
          fileSize: file.size,
          callsite: "image_upload_too_large",
        });
        return;
      }
      if (file.type === "image/gif" && file.size > 5000000) {
        reject({
          message: "GIF file size too big. Must be less than 5MB.",
          fileSize: file.size,
          callsite: "gif_upload_too_large",
        });
        return;
      }
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        if (img.width > 10000 || img.height > 10000) {
          reject({
            message:
              "Image must be less than 10,000 pixels in both width and height.",
            callsite: "image_upload_too_big",
          });
        } else {
          resolve();
        }
      };
      img.onerror = () => {
        reject({
          message: "Invalid image file",
          callsite: "image_upload_invalid_file",
        });
      };
    },
  );
};

const useS3UploadShared = (options?: {
  onUploadProgress?: (fileId: string, progress: number) => void;
  onUploadComplete?: (upload: S3Upload) => void;
  onUploadFailed?: (error: Error, fileId: string) => void;
  isAsync?: boolean;
  bucket?: PresignedS3PostDataBucket;
  enforceFileSizeLimit?: boolean;
  fileSizeLimitBytes?: number;
}): {
  addErrorToast: ReturnType<typeof useToast>["addErrorToast"];
  uploadingFiles: Set<string>;
  setUploadingFiles: React.Dispatch<React.SetStateAction<Set<string>>>;
  validateAndGetUploadData: (
    file: File,
  ) => Promise<PresignedS3UploadDataFragment>;
  uploadToS3: (
    file: File,
    uploadData: PresignedS3UploadDataFragment,
    fileId: string,
  ) => Promise<S3Upload | undefined>;
} => {
  const { addErrorToast } = useToast();
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());
  const {
    onUploadProgress,
    onUploadComplete,
    onUploadFailed,
    isAsync = false,
    bucket = PresignedS3PostDataBucket.Media,
    enforceFileSizeLimit = true,
    fileSizeLimitBytes = MAX_FILE_SIZE_BYTES,
  } = options || {};

  const [generatePresignedS3PostData] = useGeneratePresignedS3PostDataMutation({
    onError: () => {
      addErrorToast({ callsite: "bulk_upload_media_upload_data" });
    },
  });

  const validateAndGetUploadData = useCallback(
    async (file: File) => {
      if (enforceFileSizeLimit && file.size > fileSizeLimitBytes) {
        addErrorToast({
          message:
            "That file is too large. If it's a video, try uploading it to Vimeo or YouTube and pasting in the link.",
          fileSize: file.size,
          callsite: "file_upload_too_large",
        });
        throw new Error("File size too large");
      }

      const isImage = file.type.startsWith("image/");
      if (isImage) {
        await validateImage(file);
      }

      const lastDotIndex = file.name.lastIndexOf(".");
      const key =
        lastDotIndex === -1 || lastDotIndex === 0
          ? `${file.name}-${Date.now()}`
          : `${file.name.substring(0, lastDotIndex)}-${Date.now()}${file.name.substring(lastDotIndex)}`;

      const contentType = normalizeContentType(file);

      const result = await generatePresignedS3PostData({
        variables: {
          key,
          contentType,
          bucket,
        },
      });

      if (
        result.data?.generatePresignedS3PostData.success &&
        result.data.generatePresignedS3PostData.data
      ) {
        return result.data.generatePresignedS3PostData.data;
      } else {
        addErrorToast({ callsite: "bulk_upload_presigned_s3_post_data" });
        throw new Error("Failed to generate presigned S3 post data");
      }
    },
    [
      generatePresignedS3PostData,
      addErrorToast,
      bucket,
      enforceFileSizeLimit,
      fileSizeLimitBytes,
    ],
  );

  const uploadToS3 = (
    file: File,
    uploadData: PresignedS3UploadDataFragment,
    fileId: string,
  ): Promise<S3Upload | undefined> => {
    return new Promise((resolve, reject) => {
      setUploadingFiles((prev) => new Set(prev).add(fileId));
      onUploadProgress?.(fileId, 0);

      const worker = new Worker(
        new URL("../../../lib/utils/s3UploadWorker.ts", import.meta.url),
      );

      worker.onmessage = (e) => {
        switch (e.data.type) {
          case "progress": {
            const percent = e.data.percent;
            onUploadProgress?.(fileId, percent);
            break;
          }
          case "success": {
            setUploadingFiles((prev) => {
              const newSet = new Set(prev);
              newSet.delete(fileId);
              return newSet;
            });
            worker.terminate();
            const uploadedKey = uploadData.fields.key;
            const uploadedUrl = getMediaUrlFromKey(
              uploadedKey,
              uploadData.mediaDomain,
            );
            const upload: S3Upload = {
              fileId,
              url: uploadedUrl,
              name: file.name,
              key: uploadedKey,
            };
            onUploadComplete?.(upload);
            resolve(upload);
            break;
          }
          case "error": {
            setUploadingFiles((prev) => {
              const newSet = new Set(prev);
              newSet.delete(fileId);
              return newSet;
            });
            worker.terminate();
            const error = new Error("Failed to upload file");
            addErrorToast({
              message: "Failed to upload file",
              callsite: "s3_upload",
            });
            onUploadFailed?.(error, fileId);
            if (isAsync) {
              reject(error);
            } else {
              resolve(undefined);
            }
            break;
          }
        }
      };

      worker.postMessage({
        file,
        url: uploadData.url,
        fields: convertFieldsToKebabCase(uploadData.fields),
        contentType: normalizeContentType(file),
      });
    });
  };

  return {
    addErrorToast,
    uploadingFiles,
    setUploadingFiles,
    validateAndGetUploadData,
    uploadToS3,
  };
};

export const useS3Upload = (options?: {
  onUploadComplete?: (upload: S3Upload) => void;
  onUploadFailed?: (error: Error, fileId: string) => void;
  onUploadProgress?: (fileId: string, progress: number) => void;
  bucket?: PresignedS3PostDataBucket;
  enforceFileSizeLimit?: boolean;
  fileSizeLimitBytes?: number;
}): {
  handleFileUpload: (file: File, fileId?: string) => void;
  isUploading: boolean;
  uploadingFiles: string[];
} => {
  const {
    onUploadComplete,
    onUploadFailed,
    onUploadProgress,
    bucket,
    enforceFileSizeLimit,
    fileSizeLimitBytes,
  } = options || {};
  const {
    addErrorToast,
    validateAndGetUploadData,
    uploadToS3,
    uploadingFiles,
  } = useS3UploadShared({
    onUploadProgress,
    onUploadComplete,
    onUploadFailed,
    bucket,
    enforceFileSizeLimit,
    fileSizeLimitBytes,
  });

  const handleFileUpload = useCallback(
    async (file: File, fileId?: string): Promise<void> => {
      const generatedFileId = fileId || `${file.name}-${Date.now()}`;

      try {
        const uploadData = await validateAndGetUploadData(file);
        await uploadToS3(file, uploadData, generatedFileId);
      } catch (error: unknown) {
        const errorInfo = error as ToastErrorInfo;
        addErrorToast({
          iconType: "alert",
          success: false,
          callsite: "bulk_upload_presigned_s3_post_data",
          ...errorInfo,
        });
        onUploadFailed?.(
          error instanceof Error ? error : new Error(String(error)),
          generatedFileId,
        );
      }
    },
    [validateAndGetUploadData, uploadToS3, addErrorToast, onUploadFailed],
  );

  const isUploading = uploadingFiles.size > 0;

  return {
    isUploading,
    uploadingFiles: Array.from(uploadingFiles),
    handleFileUpload,
  };
};

export const useS3UploadAsync = (options?: {
  onUploadProgress?: (fileId: string, progress: number) => void;
  bucket?: PresignedS3PostDataBucket;
  enforceFileSizeLimit?: boolean;
  fileSizeLimitBytes?: number;
}): {
  handleFileUpload: (
    file: File,
    fileId?: string,
  ) => Promise<S3Upload | undefined>;
  isUploading: boolean;
  uploadingFiles: string[];
} => {
  const { onUploadProgress, bucket, enforceFileSizeLimit, fileSizeLimitBytes } =
    options || {};
  const {
    addErrorToast,
    uploadingFiles,
    validateAndGetUploadData,
    uploadToS3,
  } = useS3UploadShared({
    onUploadProgress,
    isAsync: true,
    bucket,
    enforceFileSizeLimit,
    fileSizeLimitBytes,
  });

  const handleFileUpload = useCallback(
    async (file: File, fileId?: string): Promise<S3Upload | undefined> => {
      const generatedFileId = fileId || `${file.name}-${Date.now()}`;

      try {
        const uploadData = await validateAndGetUploadData(file);
        return await uploadToS3(file, uploadData, generatedFileId);
      } catch (error: unknown) {
        const errorInfo = error as ToastErrorInfo;
        addErrorToast({
          iconType: "alert",
          success: false,
          callsite: "bulk_upload_presigned_s3_post_data",
          ...errorInfo,
        });
        const enhancedError =
          error instanceof Error ? error : new Error(String(error));
        (enhancedError as S3UploadError).fileId = generatedFileId;
        throw enhancedError;
      }
    },
    [validateAndGetUploadData, uploadToS3, addErrorToast],
  );

  const isUploading = uploadingFiles.size > 0;

  return {
    isUploading,
    uploadingFiles: Array.from(uploadingFiles),
    handleFileUpload,
  };
};

gql`
  fragment PresignedS3UploadData on PresignedS3PostData {
    url
    mediaDomain
    fields {
      key
      xAmzAlgorithm
      xAmzCredential
      xAmzDate
      xAmzSignature
      xAmzSecurityToken
      policy
    }
  }

  mutation GeneratePresignedS3PostData(
    $key: String!
    $contentType: String!
    $bucket: PresignedS3PostDataBucket!
  ) {
    generatePresignedS3PostData(
      key: $key
      contentType: $contentType
      bucket: $bucket
    ) {
      success
      data {
        ...PresignedS3UploadData
      }
      error {
        code
      }
    }
  }
`;
